import { useMemo, useEffect } from 'react'

import { Grid } from '@mui/material'
import { FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { Control } from 'react-hook-form/dist/types/form'

import { ControlledSelector, ControlledTextField, ISelectorOptions } from 'components/common'
import { representsOutOfHours } from 'constants/EntityConstants/OtherChargeEntity'
import { useGetOtherChargesQuery } from 'generated/graphql'

import { TChargeToolbarForm } from './chargeToolbar.types'

interface IProps {
  control: Control<TChargeToolbarForm, any>
  watch: UseFormWatch<TChargeToolbarForm>
  setValue: UseFormSetValue<TChargeToolbarForm>
  errors: FieldErrors<TChargeToolbarForm>
  disabled?: boolean
  hasOutOfHoursCharge?: boolean
  outOfHoursRate?: number | null
}

const OtherChargeOptions = (props: IProps) => {
  const { control, watch, setValue, errors, disabled, hasOutOfHoursCharge, outOfHoursRate } = props

  const selectedOtherChargeId = watch('otherChargeId')

  const { data: otherChargesData } = useGetOtherChargesQuery()

  const chargeOptions: ISelectorOptions = useMemo(
    () =>
      otherChargesData?.otherCharges.map((charge) => {
        const isUnavailable =
          !!hasOutOfHoursCharge && representsOutOfHours({ name: charge.name })

        return {
          value: charge.id,
          label: isUnavailable ? `${charge.name} (already applied)` : charge.name,
          disabled: isUnavailable,
        }
      }) || [],
    [hasOutOfHoursCharge, otherChargesData],
  )

  useEffect(() => {
    if (otherChargesData) {
      const otherCharge = otherChargesData.otherCharges.find(
        (otherCharge) => otherCharge.id === String(selectedOtherChargeId),
      )

      if (otherCharge) {
        const rate = representsOutOfHours({ name: otherCharge.name })
          ? outOfHoursRate ?? otherCharge.rate
          : otherCharge.rate

        setValue('name', otherCharge.name)
        setValue('rate', rate)
        setValue('vatCategoryId', String(otherCharge.vatCategoryId))
        otherCharge.xeroServiceCode && setValue('xeroServiceCode', otherCharge.xeroServiceCode)
        otherCharge.description && setValue('description', otherCharge.description)
      }
    }
  }, [selectedOtherChargeId, otherChargesData, outOfHoursRate, setValue])

  return (
    <>
      <Grid item xs={8} sm>
        <ControlledSelector
          control={control}
          label='Other Charge'
          name='otherChargeId'
          options={chargeOptions}
          required
          disabled={disabled}
          error={!!errors.otherChargeId}
          helperText={errors.otherChargeId?.message}
        />
      </Grid>

      <Grid item xs={4} sm minWidth='100px'>
        <ControlledTextField
          control={control}
          name='rate'
          label='Rate'
          defaultValue=''
          type='text'
          inputMode='numeric'
          pattern='[0-9]*'
          sanitizedNumber
          startAdornment='£'
          disabled={disabled}
        />
      </Grid>
    </>
  )
}

export default OtherChargeOptions
